import { useEffect, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** Helpers */
import clsx from "clsx";
import { useTranslation } from "react-i18next";

/** MUI */
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
    Box,
    Button,
    Container,
    Grid,
    Accordion as MuiAccordion,
    AccordionActions as MuiAccordionActions,
    AccordionDetails as MuiAccordionDetails,
    AccordionSummary as MuiAccordionSummary,
} from "@mui/material";
import { makeStyles, withStyles } from "@mui/styles";

/** Redux */
import { useSelector } from "react-redux";
import { getLoadingModules } from "@/src/_appStore/DB_Common";

/** UI */
const Preloader = dynamic(() => import("@/src/_common/Preloader"));
const ProgressAPI = dynamic(() => import("@/src/_common/ProgressAPI"));

const Accordion = withStyles({
    root: {
        boxShadow: "none",
        "&:not(:last-child)": {
            borderBottom: 0,
        },
        "&:before": {
            display: "none",
        },
        "&$expanded": {
            margin: "auto",
        },
    },
    expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
    root: {
        marginBottom: -1,
        minHeight: 0,
        "&$expanded": {
            minHeight: 0,
        },
    },
    content: {
        margin: "0px 0",
        "&$expanded": {
            margin: "0px 0",
        },
    },
    expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles((theme) => ({
    root: {
        padding: theme.spacing(2, 0.5),
        flexWrap: "wrap",
    },
}))(MuiAccordionDetails);

const AccordionActions = withStyles((theme) => ({
    root: {
        padding: theme.spacing(7.5, 2, 3, 2),
        flexWrap: "wrap",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0, 2, 3, 2),
            "& button": {
                width: "100%",
            },
            "& button + button": {
                marginTop: theme.spacing(2),
            },
        },
    },
}))(MuiAccordionActions);

const useStyles = makeStyles((theme) => ({
    boxedWrapper: {
        ...theme.shape.boxedWrapper,
        backgroundColor: "#F8F9FA",
        padding: theme.spacing(1.5, 2, 2, 2),
        [theme.breakpoints.down("md")]: {
            padding: theme.spacing(1.5, 1.5, 2, 1.5),
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0.5, 1, 0.5, 1),
        },
        position: "relative",
        /* if device has a touch screen */
        "@media (any-pointer: coarse)": {
            // borderColor: "#F00",
        },

        /* if device has no touch screen */
        "@media (any-pointer: fine)": {
            // borderColor: "#0F0",
        },
    },
    boxedWrapperCollapse: {
        padding: theme.spacing(1.5, 2, 1.5, 2),
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0.5, 1, 0.5, 1),
        },
    },
    btnFilters: {
        fontSize: "1.11111111rem",
        [theme.breakpoints.down("sm")]: {
            fontSize: "0.88888889rem",
            padding: theme.spacing(1),
            width: "100%",
            justifyContent: "space-between",
        },
    },
    btnFiltersCollapse: {},
    AccordionRoot: {
        backgroundColor: "transparent",
    },
    loaderWrap: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: "#FFFFFF",
        // border: "1px solid #F00",
        zIndex: 13,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 20,
        padding: theme.spacing(0, 2),
    },
    loaderWrapOpacity: {
        backgroundColor: "#FFFFFFCC",
    },
}));

const FiltersAccordion = (props) => {
    const { onApply, isToBeReset, onReset, prediction } = props;

    const classes = useStyles();
    const { t } = useTranslation();

    /** States */
    const [EXPANDED, setEXPANDED] = useState(true);
    const [LOADING, setLOADING] = useState(false);
    /** - redux */
    const loadingModules = useSelector(getLoadingModules);

    /** Effects */

    useEffect(() => {
        const _loading = ["tSections", "tTypes", "tDimensionsAndPositions"]?.some((l) => loadingModules[l]);
        setLOADING(_loading);
    }, [loadingModules]);

    /** Handlers */
    const handleExpanded = () => setEXPANDED(!EXPANDED);

    return (
        <Container maxWidth={false} disableGutters>
            <Grid
                container
                spacing={0}
                className={clsx(classes.boxedWrapper, EXPANDED || classes.boxedWrapperCollapse)}
            >
                {/* {!!LOADING ? (
                    <Box className={clsx(classes.loaderWrap, EXPANDED && classes.loaderWrapOpacity)}>
                        <Preloader type={"linear"} />
                    </Box>
                ) : null} */}

                <Button
                    id={"db-advanced-filters-btn"}
                    color={"primary"}
                    onClick={handleExpanded}
                    endIcon={!!EXPANDED ? <ExpandLess /> : <ExpandMore />}
                    className={clsx(classes.btnFilters, EXPANDED || classes.btnFiltersCollapse)}
                >
                    {t("WYBÓR ZAAWANSOWANY")}
                </Button>
                <Grid item xs={12}>
                    <Accordion
                        square
                        expanded={EXPANDED}
                        onChange={handleExpanded}
                        classes={{ root: classes.AccordionRoot }}
                    >
                        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header" tabIndex={-1} />
                        <AccordionDetails>{props.children}</AccordionDetails>
                        <AccordionActions>
                            <Button
                                color={"primary"}
                                onClick={onReset}
                                disabled={!isToBeReset}
                                size={"small"}
                                sx={{ fontSize: "0.777777778rem", height: 51 }}
                            >
                                {t("Zresetuj")}
                            </Button>
                            <Button
                                color={"primary"}
                                variant={"contained"}
                                onClick={onApply}
                                sx={{ fontSize: "0.777777778rem", height: 51 }}
                                disabled={!prediction?.table?._accepted}
                            >
                                {t("Zatwierdź")}
                            </Button>
                        </AccordionActions>
                    </Accordion>
                </Grid>
            </Grid>
        </Container>
    );
};

export default FiltersAccordion;
